<template>
  <div class="g-page__row g-page__row--bordered">
    <div class="p-new-envelope__details">
      <div class="p-new-envelope__details__title" data-vv-scope="envelope-title">
        <VInput
          type="text"
          :placeholder="$t('createEnvelope.sendForm.envelopeTitle')"
          v-model="title"
          v-validate="'required'"
          data-vv-name="title"
          :data-vv-as="$t('createEnvelope.sendForm.envelopeTitle')"
          data-vv-scope="envelope-title"
          data-ec="envelope_send"
          data-et="envelope_title_input"
          data-hj-suppress
        />
        <VMessage
          type="error"
          subtype="basic"
          v-show="errors.has('envelope-title.title')">
          <template v-slot:icon>
            <XMarkIcon />
          </template>
          <template v-slot:text>
            {{ errors.first('envelope-title.title') }}
          </template>
        </VMessage>
        <b>{{ $t('createEnvelope.sendForm.suggestions') }}:</b>
        <div class="p-new-envelope__details__labels">
          <VLabel
            v-for="template in $store.state.envelopes.creating.envelope.documents"
            type="info"
            subtype="outlined"
            :icon="true"
            :key="template.fingerprint"
            @click.native="updateTitle(template.title)"
            data-ec="envelope_send"
            data-et="envelope_title_suggestion">
            <template v-slot:icon>
              <LightbulbIcon />
            </template>
            <template v-slot:text>
              {{ template.title }}
            </template>
          </VLabel>
        </div>
      </div>
      <div class="g-page__row" v-if="$store.getters['envelopes/current'].next_remind > 0">
        <VMessage type="info" subtype="outlined" :fullwidth="true">
          <template v-slot:icon>
            <InfoCircleIcon />
          </template>
          <template v-slot:text>
            {{ $t('envelope.messageRemindersOccur.a') }}
            {{ $tc('envelope.messageRemindersOccur.b', remindDays, { remindDays }) }}
            {{ $t('envelope.messageRemindersOccur.c') }}
            {{ envelope.next_remind | formatDate('calendar') }}.
          </template>
        </VMessage>
      </div>
      <div class="g-page__row" v-if="$store.getters['envelopes/current'].next_expire && $store.getters['envelopes/current'].next_expire > 0">
        <VMessage type="info" subtype="outlined" :fullwidth="true">
          <template v-slot:icon>
            <InfoCircleIcon />
          </template>
          <template v-slot:text>
            {{ $t('envelope.messageExpiryOccurs.a') }}
            {{ $store.getters['envelopes/current'].next_expire | formatDate('calendar')}}
            {{ $t('envelope.messageExpiryOccurs.b') }}
          </template>
        </VMessage>
      </div>
      <div class="g-page__row p-new-envelope__expire-remind">
        <VButton
          type="outline-3"
          :action="openRemindersSetModal"
          data-ec="envelope_send"
          data-et="reminders_button">
          <BellIcon />
          <span>{{ $t('envelope.reminders') }}</span>
        </VButton>
        <VButton
          type="outline-3"
          :action="openEnvelopeExpireModal"
          data-ec="envelope_send"
          data-et="expiry_settings_button">
          <ClockIcon />
          <span>{{ $t('envelope.expirySettings') }}</span>
        </VButton>
      </div>
      <div class="p-new-envelope__details__buttons">
        <p>
          <VMessage
            type="warning"
            :fullwidth="true"
            v-if="$store.getters['company/company'].email_verified === false">
            <template v-slot:icon>
              <ExclamationTriangleIcon />
            </template>
            <template v-slot:text>
              {{ $t('envelopeSend.verifyCompanyEmail') }}
            </template>
          </VMessage>
        </p>
        <VButton
          :action="addFields"
          size="large"
          type="outline-1"
          :loading="addFieldsLoading"
          data-ec="envelope_send"
          data-et="add_fields">
          <span>{{ $t('createEnvelope.sendForm.addFields') }}</span>
          <ListOlIcon
            height="20"
            width="20"
          />
        </VButton>
        <VButton
          v-if="previewKey"
          class="ml-base"
          :href="`${signingPageUrl}/preview/${envelope.fingerprint}/${envelope.parties[0].status.fingerprint}#${previewKey}`"
          target="_blank"
          size="large"
          type="outline-1"
          :title="$t('functional.preview')"
          data-ec="envelope_send"
          data-et="preview">
          <span>{{ $t('functional.preview') }}</span>
          <EyeIcon
            height="20"
            width="20"
          />
        </VButton>
        <VButton
          :action="sendEnvelopeCheck"
          size="large"
          type="cta-1"
          class="float-right"
          :disabled="isLoading || !$store.getters['company/company'].email_verified"
          data-ec="envelope_send"
          data-et="send_envelope">
          <span>{{ $t('createEnvelope.sendForm.sendEnvelope') }}</span>
          <PaperPlaneIcon
            height="20"
            width="20"
          />
        </VButton>
      </div>
    </div>
    <ConfirmNoFieldsBeforeSendModal
      :show="showConfirmNoFieldsBeforeSendModal"
      @close="showConfirmNoFieldsBeforeSendModal = false;"
      @send="sendEnvelope"
      @addFields="addFields"
    />
    <EnvelopeExpireModal
      :envelope="envelope"
      :show="showEnvelopeExpireModal"
      @close="showEnvelopeExpireModal = false;"
      :creating="true"
    />
    <RemindersSetModal
      :show="showRemindersSetModal"
      :envelope="envelope"
      :setReminders="reminders"
      :nextRemind="envelope.next_remind"
      :remindDays="remindDays"
      :creating="true"
      @openCancelRemindersModal="showRemindersCancelModal = true;"
      @close="showRemindersSetModal = false;"
    />
    <RemindersCancelModal
      :show="showRemindersCancelModal"
      :envelope="envelope"
      :setReminders="reminders"
      :creating="true"
      @openSetRemindersModal="showRemindersSetModal = true;"
      @close="showRemindersCancelModal = false;"
    />
  </div>
</template>

<script>
import EnvelopeExpireModal from '@/views/modals/envelope/EnvelopeExpireModal.vue';
import RemindersSetModal from '@/views/modals/reminder/RemindersSetModal.vue';
import RemindersCancelModal from '@/views/modals/reminder/RemindersCancelModal.vue';
import ConfirmNoFieldsBeforeSendModal from '@/components/ui/envelopeCreate/ConfirmNoFieldsBeforeSendModal.vue';
import VButton from '@/components/deprecated/atoms/Button.vue';
import VInput from '@/components/deprecated/atoms/Input.vue';
import VLabel from '@/components/deprecated/atoms/Label.vue';
import VMessage from '@/components/deprecated/atoms/Message.vue';
import { mapGetters } from 'vuex';
import {
  BellIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  InfoCircleIcon,
  LightbulbIcon,
  ListOlIcon,
  PaperPlaneIcon,
  XMarkIcon,
} from '@/components/icons/fontIcons';

export default {
  name: 'VSendForm',
  description: 'Step page for sending the created envelope',
  inject: {
    $validator: '$validator',
  },
  components: {
    ConfirmNoFieldsBeforeSendModal,
    EnvelopeExpireModal,
    RemindersSetModal,
    RemindersCancelModal,
    VButton,
    VInput,
    VLabel,
    VMessage,
    BellIcon,
    ClockIcon,
    EyeIcon,
    ListOlIcon,
    PaperPlaneIcon,
    XMarkIcon,
    LightbulbIcon,
    InfoCircleIcon,
    ExclamationTriangleIcon,
  },
  data() {
    return {
      showConfirmNoFieldsBeforeSendModal: false,
      showEnvelopeExpireModal: false,
      showRemindersSetModal: false,
      showRemindersCancelModal: false,
      addFieldsLoading: false,
      reminders: {
        loading: false,
        radio: null,
        custom: null,
        days: null,
      },
      modalLoading: false,
      auditLog: {
        loading: true,
      },
      requestsInProgress: 0,
      title: this.$store.getters['envelopes/current'].title,
      signingPageUrl: process.env.VUE_APP_SIGNING_PAGE,
    };
  },
  computed: {
    ...mapGetters({
      previewKey: 'auth/previewKey',
    }),
    remindDays() {
      if (this.envelope.auto_remind_hours > 0) {
        return this.envelope.auto_remind_hours / 24;
      }
      return null;
    },
    isLoading() {
      return this.requestsInProgress > 0;
    },
    envelope() {
      return this.$store.getters['envelopes/current'];
    },
    hasFields() {
      return this.$store.getters['envelopes/hasFields'];
    },
  },
  mounted() {
    if (!this.$store.getters['company/company'].email_verified) {
      this.$store.dispatch('company/GET');
    }

    const firstDocumentTitle = this.$store.state.envelopes.creating.envelope.documents[0].title;
    if ((!this.title || this.title === 'New Envelope') && firstDocumentTitle) this.title = firstDocumentTitle;

    this.$store.dispatch('auth/GET_PREVIEW_KEY');
  },
  methods: {
    updateTitle(title) {
      this.title = title;
    },
    openRemindersSetModal() {
      this.showRemindersSetModal = true;
    },
    openEnvelopeExpireModal() {
      this.showEnvelopeExpireModal = true;
    },
    async addFields() {
      this.addFieldsLoading = true;
      this.requestsInProgress += 1;
      const valid = await this.$validator.validateAll('envelope-title');
      this.requestsInProgress -= 1;

      if (!valid) return null;

      const { fingerprint } = this.envelope;
      const data = {
        title: this.title,
      };

      try {
        this.requestsInProgress += 1;
        await this.$store.dispatch('envelopes/UPDATE_ENVELOPE', {
          fingerprint,
          data,
        });
        this.requestsInProgress -= 1;
      } catch (error) {
        if (!error.response) throw error;
        this.requestsInProgress -= 1;
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.update', { name: 'envelope' }),
          icon: 'times',
          type: 'error',
        }));
        this.addFieldsLoading = false;
        return false;
      }

      this.$router.push({
        name: 'EnvelopeUpdate',
        params: { envelopeFingerprint: fingerprint },
      });
      this.addFieldsLoading = false;
      return null;
    },
    async sendEnvelopeCheck() {
      if (!await this.$validator.validateAll('envelope-title')) return null;
      if (this.hasFields) return this.sendEnvelope();

      this.showConfirmNoFieldsBeforeSendModal = true;
      return null;
    },
    async sendEnvelope() {
      try {
        this.requestsInProgress += 1;
        await this.$store.dispatch('envelopes/SEND', { title: this.title });
        this.requestsInProgress -= 1;
      } catch (error) {
        this.requestsInProgress -= 1;
        if (!error.response) throw error;
        this.$bus.$emit('renderGlobalMessage', ({
          message: this.$t('message.fail.send', { name: 'envelope' }),
          icon: 'times',
          type: 'error',
        }));

        return false;
      }

      this.$router.push({
        name: 'EnvelopeStatus',
        params: { envelopeFingerprint: this.envelope.fingerprint },
      });
      return null; // TODO why are these functions async?
    },
  },
};
</script>

<style scoped>
.g-page__row--bordered {
  padding-top: var(--spacing-base);
}

.p-new-envelope {
  padding-bottom: 117px;
}

.p-new-envelope__details {
  margin: 0 auto;
  padding: 15px;
  background: #fff;
  border: 1px solid var(--color-border);
}

.p-new-envelope__details:after {
  content: '';
  display: block;
  clear: both;
}

.p-new-envelope__details__title:after {
  content: '';
  display: block;
  clear: both;
}

.p-new-envelope__details__title b {
  display: block;
}

.p-new-envelope__details__title ::v-deep .a-input {
  margin-bottom: var(--spacing-base);
}

.p-new-envelope__details__labels {
  margin-bottom: var(--spacing-large);
}

.p-new-envelope__details__labels:after {
  content: '';
  display: block;
  clear: both;
}

.p-new-envelope__details__labels ::v-deep .a-label {
  cursor: pointer;
  margin-top: var(--spacing-small);
}

.p-new-envelope__details__labels ::v-deep .a-label__text, .p-new-envelope__details__labels ::v-deep .a-label__icon,
.p-new-envelope__details__labels ::v-deep .a-label svg,
.p-new-envelope__details__labels ::v-deep .a-label span {
  background: transparent;
  pointer-events: none;
}

.p-new-envelope__details__buttons {
  margin-top: var(--spacing-base);
  border-top: 1px solid var(--color-border);
}

.p-new-envelope__details__buttons .a-message {
  margin-top: 20px;
}

.p-new-envelope__details__buttons:after {
  content: '';
  display: block;
  clear: both;
}

@media only screen and (max-width: 600px) {
  .p-new-envelope__details__buttons {
    display: flex;
    flex-direction: column;
  }
  .p-new-envelope__details__buttons a {
    width: 250px;
    margin-left: 0 !important;
    margin: var(--spacing-xs) 0;
  }
}

.p-new-envelope__details ::v-deep .a-input {
  font-size: 20px;
}

.p-new-envelope__expire-remind ::v-deep .a-button {
  margin-left: var(--spacing-base);
}

.p-new-envelope__expire-remind ::v-deep .a-button:nth-of-type(1) {
  margin-left: 0;
}

@media only screen and (max-width: 380px) {
  .p-new-envelope__expire-remind {
    display: flex;
    flex-direction: column;
  }
  .p-new-envelope__expire-remind a {
    width: 200px;
    margin-left: 0 !important;
    margin: var(--spacing-xs) 0;
  }
}

</style>
